import { v4 as uuidv4 } from 'uuid';

// Minimal types needed for this utility
type MessageForStorage = {
    [key: string]: unknown; // Allow flexible structure but expect timestamp/created_at
    timestamp?: string | Date;
    created_at?: string | Date;
};

const log = (context: string, message: string, data?: unknown) => {
    // Basic console logging; replace with a more robust logger if needed
    console.log(`[ChatLocalStorage:${context}] ${message}`, data ? JSON.stringify(data).substring(0, 150) + '...' : '');
};

// --- Key Generation ---
const getKeyPrefix = (chatbotId: string, sessionId: string): string => {
    if (!chatbotId || !sessionId) {
        console.error('[ChatLocalStorage:getKeyPrefix] Chatbot ID and Session ID are required.');
        return 'chatflow_invalid_invalid'; // Return a non-functional prefix
    }
    return `chatflow_${chatbotId}_${sessionId}`;
};

export const getChatStorageKeys = (chatbotId: string, sessionId: string) => {
    const prefix = getKeyPrefix(chatbotId, sessionId);
    return {
        prefix,
        messages: `${prefix}_messages`,
        index: `${prefix}_currentQuestionIndex`,
        completed: `${prefix}_flowCompleted`,
        responses: `${prefix}_userResponses`,
        validationAttempts: `${prefix}_validationAttempts`,
        conversationContext: `${prefix}_conversationContext`,
        followUpAck: `${prefix}_followUpAck`,
        // Session ID key is simpler and chatbot-specific
        sessionId: `chatflow_${chatbotId}_sessionId`,
    };
};

// --- Session ID Management ---
export const getSessionId = (chatbotId: string): string => {
    if (!chatbotId) {
        console.error('[ChatLocalStorage:getSessionId] Chatbot ID is required.');
        return `invalid-session-${uuidv4()}`; // Return a non-persistent ID
    }

    // Check for existing session ID
    const key = `chatflow_${chatbotId}_sessionId`;
    let sessionId = localStorage.getItem(key);

    // Check for session expiration
    const expirationKey = `chatflow_${chatbotId}_sessionExpiration`;
    const sessionExpiration = localStorage.getItem(expirationKey);
    const now = new Date().getTime();

    // If session exists but has expired, create a new one
    if (sessionId && sessionExpiration && parseInt(sessionExpiration) < now) {
        log('getSessionId', `Session expired for ${chatbotId}, creating new session`);
        sessionId = null; // Force creation of new session

        // Clear any data associated with the expired session
        try {
            // Find and remove all keys associated with this session
            Object.keys(localStorage)
                .filter(storageKey => storageKey.includes(`chatflow_${chatbotId}_${sessionId}`))
                .forEach(storageKey => localStorage.removeItem(storageKey));
        } catch (error) {
            console.error(`[ChatLocalStorage:getSessionId] Error clearing expired session data:`, error);
        }
    }

    // Create new session if needed
    if (!sessionId) {
        sessionId = uuidv4();
        try {
            // Set the session ID
            localStorage.setItem(key, sessionId);

            // Set session expiration (24 hours from now)
            const expiration = new Date().getTime() + (24 * 60 * 60 * 1000);
            localStorage.setItem(expirationKey, expiration.toString());

            log('getSessionId', `Created new Session ID for ${chatbotId}: ${sessionId} (expires: ${new Date(expiration).toISOString()})`);
        } catch (error) {
            console.error(`[ChatLocalStorage:getSessionId] Failed to save new Session ID for key ${key}:`, error);
            // Return the generated ID even if saving failed, but it won't persist
        }
    } else {
        log('getSessionId', `Retrieved Session ID for ${chatbotId}: ${sessionId}`);
    }

    return sessionId;
};

// New function to force creation of a new session ID
export const createNewSessionId = (chatbotId: string): string => {
    if (!chatbotId) {
        console.error('[ChatLocalStorage:createNewSessionId] Chatbot ID is required.');
        return `invalid-session-${uuidv4()}`; // Return a non-persistent ID
    }

    // Get the old session ID for cleanup
    const key = `chatflow_${chatbotId}_sessionId`;
    const oldSessionId = localStorage.getItem(key);

    // Generate new session ID
    const newSessionId = uuidv4();

    try {
        // Clear any data associated with the old session
        if (oldSessionId) {
            log('createNewSessionId', `Clearing data for old session ${oldSessionId}`);
            Object.keys(localStorage)
                .filter(storageKey => storageKey.includes(`chatflow_${chatbotId}_${oldSessionId}`))
                .forEach(storageKey => {
                    log('createNewSessionId', `Removing key: ${storageKey}`);
                    localStorage.removeItem(storageKey);
                });
        }

        // Set the new session ID
        localStorage.setItem(key, newSessionId);

        // Set session expiration (24 hours from now)
        const expirationKey = `chatflow_${chatbotId}_sessionExpiration`;
        const expiration = new Date().getTime() + (24 * 60 * 60 * 1000);
        localStorage.setItem(expirationKey, expiration.toString());

        log('createNewSessionId', `Created and saved NEW Session ID for ${chatbotId}: ${newSessionId} (expires: ${new Date(expiration).toISOString()})`);
    } catch (error) {
        console.error(`[ChatLocalStorage:createNewSessionId] Failed to save new Session ID for key ${key}:`, error);
        // Return the generated ID even if saving failed, but it won't persist
    }

    return newSessionId;
};

// --- State Saving ---
export const saveChatState = <T>(key: string, value: T): void => {
    if (!key) {
        console.error('[ChatLocalStorage:saveState] Storage key is required.');
        return;
    }
    try {
        let valueToStore: unknown = value;
        // Special handling for Date objects in messages array
        if (key.includes('_messages') && Array.isArray(value)) {
            valueToStore = (value as MessageForStorage[]).map(msg => ({
                ...msg,
                timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
                created_at: msg.created_at instanceof Date ? msg.created_at.toISOString() : msg.created_at
            }));
        }

        const stringifiedValue = JSON.stringify(valueToStore);
        localStorage.setItem(key, stringifiedValue);
        log('saveState', `SAVED Key: ${key}`); // Keep log concise
    } catch (error) {
        console.error(`[ChatLocalStorage:saveState] Error saving state for key ${key}:`, error);
    }
};

// --- State Loading ---
export const loadChatState = <T>(key: string, defaultValue: T): T => {
     if (!key) {
        console.error('[ChatLocalStorage:loadState] Storage key is required.');
        return defaultValue;
    }
    try {
        const savedValue = localStorage.getItem(key);
        // log('loadState', `LOAD Attempt Key: ${key}, Raw:`, savedValue); // Verbose logging
        if (savedValue !== null) {
            let parsedValue = JSON.parse(savedValue);

            // Special handling for Date objects in messages array
            if (key.includes('_messages') && Array.isArray(parsedValue)) {
                parsedValue = parsedValue.map((msg: MessageForStorage) => ({
                    ...msg,
                    timestamp: msg.timestamp ? new Date(msg.timestamp) : undefined,
                    created_at: msg.created_at ? new Date(msg.created_at) : undefined
                }));
            }
            log('loadState', `LOADED Key: ${key}`); // Keep log concise
            return parsedValue as T;
        }
    } catch (error) {
        console.error(`[ChatLocalStorage:loadState] Error getting saved state for key ${key}:`, error);
    }
    log('loadState', `DEFAULT Key: ${key}`);
    return defaultValue;
};

// --- State Clearing ---
export const clearChatState = (chatbotId: string, sessionId: string): void => {
    if (!chatbotId || !sessionId) {
         console.error('[ChatLocalStorage:clearChatState] Chatbot ID and Session ID are required.');
        return;
    }
    const keys = getChatStorageKeys(chatbotId, sessionId);
    // Define keys associated with a specific session
    const sessionKeysToClear = [
        keys.messages,
        keys.index,
        keys.completed,
        keys.responses,
        keys.validationAttempts,
        keys.conversationContext,
        keys.followUpAck,
    ];
    log('clearState', `Clearing session keys for chatbot ${chatbotId}, session ${sessionId}`);
    sessionKeysToClear.forEach(key => {
        try {
            localStorage.removeItem(key);
            // log('clearState', `Removed: ${key}`); // Optional verbose logging
        } catch (error) {
             console.error(`[ChatLocalStorage:clearState] Error removing key ${key}:`, error);
        }
    });

    // Optionally clear the session ID itself if you want a completely new session on reset
    // try {
    //     localStorage.removeItem(keys.sessionId);
    //     log('clearState', `Removed session ID key: ${keys.sessionId}`);
    // } catch (error) {
    //     console.error(`[ChatLocalStorage:clearState] Error removing session ID key ${keys.sessionId}:`, error);
    // }
};